/**
 * @blockcast/transport - Transport Manager
 *
 * Unified transport abstraction for MoQ players supporting:
 * - window.multicast gateway subscriptions
 * - AMT proxy tunneling (RFC 7450) with optional DRIAD relay discovery
 * - WebTransport/QUIC (MoQ-lite protocol)
 * - WebSocket fallback
 *
 * Transport promotion hierarchy (WebTransport-first for fast startup):
 * 1. WebTransport - immediate connection for fast channel select
 * 2. Background discovery - check if window.multicast is available
 * 3. Promote to gateway multicast if available
 * 4. WebSocket fallback if WebTransport unavailable
 *
 * Features:
 * - Progressive adaptive bitrate based on network conditions
 * - FEC (Forward Error Correction) for burst loss recovery
 * - Seamless transport switching with session continuity
 * - Unified FEC/ABR pipeline across all transport modes
 */

import type {
	TransportConfig,
	TransportConnectConfig,
	TransportSubscription,
	TransportCapabilities,
	TransportState,
	QualityLevel,
	WindowMulticastCapabilities,
	WindowMulticastGateway,
	WindowMulticastSubscriptionState,
} from "./types.js";
import { AdaptiveBitrateController } from "./abr-controller.js";
import {
	MulticastOrchestrator,
	type MulticastTierPacket,
	type OrchestratorCommand,
	type OrchestratorOptions,
} from "./multicast-orchestrator.js";
import type { MulticastTierEndpoint } from "./multicast-tier.js";
import { multicastSubscriptionPool } from "./multicast-subscription-pool.js";
import { normalizeMulticastEndpoint } from "./endpoint-config.js";

/**
 * Transport Manager
 *
 * Manages transport connections with automatic mode selection:
 * WebTransport -> window.multicast -> AMT proxy -> WebSocket
 */
export class TransportManager {
	private state: TransportState = {
		mode: "disconnected",
		connected: false,
		packetsReceived: 0,
		bytesReceived: 0,
		lastPacketTime: 0,
	};

	private capabilities: TransportCapabilities | null = null;
	private subscription: TransportSubscription | null = null;
	/** Built by `connect()` from the caller's ladder; there is no built-in one. */
	private abr: AdaptiveBitrateController | null = null;
	private multicastOrchestrator: MulticastOrchestrator | null = null;
	private onOrchestratorCommand?: (command: OrchestratorCommand) => void;
	private connectionGeneration = 0;

	private onPacket?: (data: ArrayBuffer) => void;
	private onError?: (error: Error) => void;
	private onStateChange?: (state: TransportState) => void;

	private getWindow(): Window | undefined {
		return typeof window === "undefined" ? undefined : window;
	}

	private getWindowMulticastGateway(): WindowMulticastGateway | undefined {
		return this.getWindow()?.multicast;
	}

	private async getGatewayCapabilities(
		gateway: WindowMulticastGateway,
	): Promise<WindowMulticastCapabilities | undefined> {
		if (!gateway?.capabilities) return undefined;
		try {
			return typeof gateway.capabilities === "function" ?
				await gateway.capabilities() :
				gateway.capabilities;
		} catch (error) {
			console.warn("[Transport] window.multicast capabilities failed:", error);
			return undefined;
		}
	}

	private async gatewaySupportsBytes(gateway: WindowMulticastGateway | undefined): Promise<boolean> {
		if (typeof gateway?.subscribe !== "function") return false;
		if (!gateway.capabilities) return true;

		const capabilities = await this.getGatewayCapabilities(gateway);
		if (!capabilities) return false;
		const consumeModes = capabilities.consumeModes;
		return !consumeModes || consumeModes.includes("bytes");
	}

	/**
	 * Detect available transport capabilities
	 */
	async detectCapabilities(): Promise<TransportCapabilities> {
		if (this.capabilities) return this.capabilities;

		const gateway = this.getWindowMulticastGateway();
		const hasGatewayBytes = await this.gatewaySupportsBytes(gateway);
		const win = this.getWindow();

		this.capabilities = {
			nativeSSM: hasGatewayBytes,
			nativeIGMP: hasGatewayBytes,
			directSockets: typeof win?.UDPSocket !== "undefined",
			amt: false,
			driad: hasGatewayBytes, // the gateway runs RFC 8777 discovery
			fecDecode: false,
		};

		console.log("[Transport] Capabilities:", this.capabilities);
		return this.capabilities;
	}

	/**
	 * Connect using best available transport
	 *
	 * Promotion hierarchy:
	 * 1. window.multicast gateway bytes
	 * 2. AMT: a configured relay via the AMT proxy, else gateway DRIAD discovery
	 * 3. MOQ WebTransport (final fallback)
	 *
	 * The ABR ladder (`abrLadder`) and initial rendition (`initialQuality`)
	 * are checked first, before anything is torn down. A config that fails is
	 * refused with the same shape as an invalid endpoint, the error to the
	 * error callback and `false` resolved; unlike one, it is caught before the
	 * live subscription closes, so a refused reconnect leaves that subscription,
	 * its state and any connect already in flight untouched.
	 */
	async connect(config: TransportConnectConfig): Promise<boolean> {
		let abr: AdaptiveBitrateController;
		try {
			abr = new AdaptiveBitrateController({
				ladder: config.abrLadder,
				initialQuality: config.initialQuality?.name,
			});
		} catch (error) {
			// A refused config is a caller bug. Without an error callback, say so
			// on the console, since `false` alone also means "no multicast path";
			// with one, the callback is the report.
			const refused = error instanceof Error ? error : new Error(String(error));
			if (this.onError) this.onError(refused);
			else console.error("[Transport] ABR config refused, not connecting:", refused);
			return false;
		}
		const generation = ++this.connectionGeneration;
		this.subscription?.close();
		this.subscription = null;
		// The previous connection's ladder ends with it, as in disconnect(): a
		// reconnect that bails below (invalid endpoint, or superseded while
		// detecting capabilities) leaves no ladder, and only the success path
		// installs `abr`.
		this.abr = null;
		this.state = {
			mode: "disconnected",
			connected: false,
			packetsReceived: 0,
			bytesReceived: 0,
			lastPacketTime: 0,
		};
		this.onStateChange?.(this.state);
		const endpoint = normalizeMulticastEndpoint({
			groupAddress: config.group,
			sourceAddress: config.source,
			port: config.port,
			mode: config.mode,
			amtRelay: config.amtRelay,
			amtRelayPort: config.amtRelayPort,
		});
		if (!endpoint) {
			const error = new Error("Invalid multicast endpoint configuration");
			this.onError?.(error);
			return false;
		}
		config = {
			...config,
			group: endpoint.groupAddress,
			source: endpoint.sourceAddress ?? "",
			mode: endpoint.mode,
			amtRelay: endpoint.amtRelay,
			amtRelayPort: endpoint.amtRelayPort,
		};
		const caps = await this.detectCapabilities();
		if (generation !== this.connectionGeneration) return false;

		// ABR adapts over the caller's ladder (the catalog's renditions),
		// validated above before anything was torn down.
		this.abr = abr;

		abr.setCallback((quality) => {
			this.state.quality = quality;
			this.subscription?.onqualitychange?.(quality);
			this.onStateChange?.(this.state);
		});

		// Try transport hierarchy
		let connected = false;

		// 1. Try the canonical browser multicast gateway.
		if (caps.nativeSSM || caps.nativeIGMP) {
			console.log("[Transport] Attempting window.multicast...");
			connected = await this.connectWindowMulticast(config, generation);
			if (connected) {
				if (generation !== this.connectionGeneration) return false;
				this.state.mode = "multicast";
				return true;
			}
		}

		// 2. Try AMT (configured relay, else gateway DRIAD discovery)
		if (!connected) {
			console.log("[Transport] Attempting AMT tunnel...");
			connected = await this.connectAmt(config, generation);
			if (connected) {
				if (generation !== this.connectionGeneration) return false;
				this.state.mode = "amt";
				return true;
			}
		}

		// 3. Fallback to MOQ
		console.log("[Transport] All multicast transports failed, falling back to MOQ");
		return false;
	}

	/**
	 * Connect via the lowercase window.multicast bytes gateway.
	 */
	private async connectWindowMulticast(
		config: TransportConfig,
		generation: number,
		mode: "multicast" | "amt" = "multicast",
	): Promise<boolean> {
		try {
			const gateway = this.getWindowMulticastGateway();
			if (typeof gateway?.subscribe !== "function") return false;

			const lease = await multicastSubscriptionPool.acquire(gateway, config, {
				onBytes: (data) => {
					if (generation === this.connectionGeneration) this.handlePacket(data);
				},
				onState: (state: WindowMulticastSubscriptionState) => {
					if (generation !== this.connectionGeneration) return;
					this.state.connected = state !== "closed";
					this.onStateChange?.(this.state);
				},
				onError: (error) => {
					if (generation !== this.connectionGeneration) return;
					console.error("[Transport] window.multicast error:", error);
					this.onError?.(error);
				},
			});
			if (generation !== this.connectionGeneration) {
				await lease.release();
				return false;
			}

			this.subscription = {
				id: lease.key,
				config,
				close: () => { void lease.release(); },
			};

			this.state = {
				mode,
				connected: true,
				group: config.group,
				source: config.source,
				quality: this.requireAbr().getCurrentQuality(),
				packetsReceived: 0,
				bytesReceived: 0,
				lastPacketTime: 0,
			};

			console.log(`[Transport] Connected via window.multicast: (${config.source}, ${config.group})`);
			this.onStateChange?.(this.state);
			return true;
		} catch (error) {
			console.warn("[Transport] window.multicast failed:", error);
			return false;
		}
	}

	/**
	 * Connect via AMT: a configured relay goes through the AMT proxy; with no
	 * relay, the window.multicast gateway discovers one per RFC 8777 (DRIAD).
	 */
	private async connectAmt(config: TransportConfig, generation: number): Promise<boolean> {
		try {
			if (config.amtRelay) return await this.connectAmtProxy(config, config.amtRelay, generation);
			if (config.driadEnabled === false || !config.source || config.mode === "native") {
				console.warn("[Transport] No AMT relay available (DRIAD requires source address for SSM)");
				return false;
			}
			console.log(`[Transport] Asking the gateway for a DRIAD relay for source ${config.source}...`);
			return await this.connectWindowMulticast({ ...config, amtDiscovery: "driad" }, generation, "amt");
		} catch (error) {
			console.warn("[Transport] AMT connection failed:", error);
			return false;
		}
	}

	/**
	 * Connect via WebSocket-based AMT proxy (browser fallback)
	 */
	private async connectAmtProxy(config: TransportConfig, amtRelay: string, generation: number): Promise<boolean> {
		try {
			const relayHost = amtRelay.includes(":") && !amtRelay.startsWith("[") ? `[${amtRelay}]` : amtRelay;
			// amtRelayPort is the relay's RFC 7450 UDP port, not the proxy's HTTPS port.
			const wsUrl = new URL(`wss://${relayHost}/amt-proxy`);
			wsUrl.searchParams.set("group", config.group);
			wsUrl.searchParams.set("source", config.source || "");
			wsUrl.searchParams.set("port", String(config.port));

			const ws = new WebSocket(wsUrl);

			return new Promise((resolve, reject) => {
				ws.binaryType = "arraybuffer";
				const timeout = setTimeout(() => {
					ws.close();
					reject(new Error("AMT proxy connection timeout"));
				}, 10000);

				ws.onopen = () => {
					clearTimeout(timeout);
					if (generation !== this.connectionGeneration) {
						ws.close();
						resolve(false);
						return;
					}
					console.log(`[Transport] Connected to AMT proxy: ${amtRelay}`);

					this.subscription = {
						id: `amt-proxy-${Date.now()}`,
						config,
						close: () => ws.close(),
					};

					this.state = {
						mode: "amt",
						connected: true,
						group: config.group,
						source: config.source,
						amtRelay,
						quality: this.requireAbr().getCurrentQuality(),
						packetsReceived: 0,
						bytesReceived: 0,
						lastPacketTime: 0,
					};

					this.onStateChange?.(this.state);
					resolve(true);
				};

				ws.onmessage = (event) => {
					if (generation === this.connectionGeneration && event.data instanceof ArrayBuffer) {
						this.handlePacket(event.data);
					}
				};

				ws.onerror = (error) => {
					clearTimeout(timeout);
					if (generation !== this.connectionGeneration) {
						resolve(false);
						return;
					}
					console.error("[Transport] AMT proxy error:", error);
					reject(error);
				};

				ws.onclose = () => {
					clearTimeout(timeout);
					if (generation !== this.connectionGeneration) {
						resolve(false);
						return;
					}
					this.state.connected = false;
					this.onStateChange?.(this.state);
					resolve(false);
				};
			});
		} catch (error) {
			console.warn("[Transport] AMT proxy failed:", error);
			return false;
		}
	}

	/**
	 * Handle received packet
	 */
	private handlePacket(data: ArrayBuffer) {
		this.state.packetsReceived++;
		this.state.bytesReceived += data.byteLength;
		this.state.lastPacketTime = Date.now();

		// Update ABR with sample
		// Packets reach here only through a connect()ed join, which built the ABR.
		this.abr?.recordSample(data.byteLength, 0);

		// Forward to callback
		this.onPacket?.(data);
	}

	/**
	 * Get current transport state
	 */
	getState(): TransportState {
		return { ...this.state };
	}

	/**
	 * The rung of the live connection's `abrLadder` ABR is on; `null` when
	 * there is no ladder (before `connect(config)` succeeds, after
	 * `disconnect()`, or after a reconnect that was torn down and bailed).
	 */
	getQuality(): QualityLevel | null {
		return this.abr?.getCurrentQuality() ?? null;
	}

	/**
	 * Force the rung named `name` of the live connection's `abrLadder`.
	 * Throws `Error` when there is no ladder (see `getQuality()`), and
	 * `RangeError` when the ladder has no rung of that name; it never falls
	 * back to another rung.
	 */
	setQuality(name: string) {
		this.requireAbr().setQuality(name);
	}

	/** The controller `connect()` built; before it there is no ladder to adapt over. */
	private requireAbr(): AdaptiveBitrateController {
		if (!this.abr) throw new Error("[Transport] ABR has no ladder before connect(config)");
		return this.abr;
	}

	/** Configures multicast-only ABR while leaving the caller's MoQ ABR untouched. */
	configureMulticastTiers(
		options: OrchestratorOptions,
		onCommand?: (command: OrchestratorCommand) => void,
	): MulticastTierEndpoint | undefined {
		this.dispatchOrchestratorCommand(this.multicastOrchestrator?.cancel());
		this.multicastOrchestrator = new MulticastOrchestrator(options);
		this.onOrchestratorCommand = onCommand;
		return this.multicastOrchestrator.start();
	}

	recordMulticastGoodput(bitrate: number | undefined): OrchestratorCommand | undefined {
		return this.dispatchOrchestratorCommand(this.multicastOrchestrator?.onGoodput({ bitrate }));
	}

	recordMulticastHeadroom(healthy: boolean): OrchestratorCommand | undefined {
		return this.dispatchOrchestratorCommand(this.multicastOrchestrator?.onHeadroom({ healthy }));
	}

	recordMulticastTierPacket(packet: MulticastTierPacket): OrchestratorCommand | undefined {
		return this.dispatchOrchestratorCommand(this.multicastOrchestrator?.onTierPacket(packet));
	}

	pollMulticastTierSwitch(): OrchestratorCommand | undefined {
		return this.dispatchOrchestratorCommand(this.multicastOrchestrator?.poll());
	}

	/** Abandon the in-flight tier join, e.g. because the host could not join the target. */
	cancelMulticastTierJoin(): OrchestratorCommand | undefined {
		return this.dispatchOrchestratorCommand(this.multicastOrchestrator?.cancel());
	}

	/**
	 * Set packet callback
	 */
	setPacketCallback(callback: (data: ArrayBuffer) => void) {
		this.onPacket = callback;
	}

	/**
	 * Set error callback
	 */
	setErrorCallback(callback: (error: Error) => void) {
		this.onError = callback;
	}

	/**
	 * Set state change callback
	 */
	setStateCallback(callback: (state: TransportState) => void) {
		this.onStateChange = callback;
	}

	/**
	 * Disconnect
	 */
	disconnect() {
		this.connectionGeneration++;
		this.subscription?.close();
		this.subscription = null;
		// No ladder after a disconnect, as before the first connect():
		// getQuality() is null and setQuality() throws until connect(config).
		this.abr = null;
		this.dispatchOrchestratorCommand(this.multicastOrchestrator?.cancel());
		this.multicastOrchestrator = null;
		this.onOrchestratorCommand = undefined;
		this.state = {
			mode: "disconnected",
			connected: false,
			packetsReceived: 0,
			bytesReceived: 0,
			lastPacketTime: 0,
		};
		this.onStateChange?.(this.state);
	}

	private dispatchOrchestratorCommand(command: OrchestratorCommand | undefined): OrchestratorCommand | undefined {
		if (command) this.onOrchestratorCommand?.(command);
		return command;
	}
}

// Export singleton instance
export const transportManager = new TransportManager();
